// eslint-disable-next-line no-unused-vars
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import { useLocation } from 'react-router-dom'

import { IconButton, makeStyles, TextField, useTheme } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import LocationOnIcon from '@material-ui/icons/LocationOn'

import AddressOption from './AddressOption'
import { MIN_SEARCH_LENGTH, PROVIDER, SEARCH_DEBOUNCE_MS, searchAddressOptions } from './addressSearch'
import useViewportSizes from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'
import track, { buscarEndereco, enderecoEncontrado, enderecoNaoEncontrado } from '~/track'
import api from '~/api'
import { Close } from '@material-ui/icons'

const TRACK_SETTLE_MS = 1500

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: 10,
  },
  inputIcon: {
    color: theme.palette.primary.main,
    fontSize: 14,
  },
  input: {
    boxShadow: theme.shadows[3],
    borderRadius: 4,
  },
  innerInput: {
    padding: 10,
  },
  paperContent: ({ isMobile }) => ({
    maxHeight: !isMobile && 200,
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    '& li': {
      paddingBlock: 8,
      borderBottom: '1px solid #F5F5F5',
    },
    ...theme.gofind.scrollBar,
  }),
  option: ({ isMobile }) =>
    isMobile
      ? {
          paddingLeft: 0,
        }
      : {},
}))

export default function SearchAddress({ size, label, placeholder, onChangeValue, onChangeLocation }) {
  const theme = useTheme()
  const viewport = useViewportSizes()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const classes = useStyles({ isMobile })
  const config = useSelector((state) => state.config)
  const position = useSelector((state) => state.position)
  const location = useLocation()

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [isNoOptions, setIsNoOptions] = useState(false)
  const [loading, setLoading] = useState(false)

  const { getLiteral } = useLiterals()

  const searchHere = useCallback(
    (value) =>
      api
        .searchLocation(value, config, position, location)
        .then((res) => res.map((item) => ({ ...item, label: api.getLabel(item, getLiteral) }))),
    [config, position, location, getLiteral],
  )

  const searchHereRef = useRef(searchHere)

  useEffect(() => {
    searchHereRef.current = searchHere
  }, [searchHere])

  const handleReset = () => {
    setOptions([])
    setLoading(false)
    setInputValue('')
  }

  const trackSearch = useMemo(
    () =>
      debounce((address, provider) => {
        buscarEndereco(address)

        if (provider === PROVIDER.here || provider === PROVIDER.google) {
          enderecoEncontrado(address, provider)
        } else {
          enderecoNaoEncontrado(address, provider)
        }
      }, TRACK_SETTLE_MS),
    [],
  )

  const handleSearch = useMemo(
    () =>
      debounce(async (q) => {
        setIsNoOptions(false)

        if (!q) {
          setLoading(false)
          return
        }

        if (q.length < MIN_SEARCH_LENGTH) return

        setLoading(true)

        try {
          const result = await searchAddressOptions(q, searchHereRef.current)

          setOptions(result.options)
          setIsNoOptions(result.isNoOptions)
          trackSearch(q, result.provider)
        } catch (error) {
          console.error('cannot search address', error.message)
          setIsNoOptions(true)
          trackSearch(q, PROVIDER.error)
        } finally {
          setLoading(false)
        }
      }, SEARCH_DEBOUNCE_MS),
    [trackSearch],
  )

  const handleInputChange = (e, item, reason) => {
    switch (reason) {
      case 'clear':
        handleReset()
        break
      case 'input':
        if (e.key === 'Enter') e.stopPropagation()

        setInputValue(item)
        break
    }
  }

  const handleSelect = async (_, item, reason) => {
    if (item && reason === 'select-option') {
      try {
        const response = await api.fetchGeoCode(item)

        const location = {
          ...item.address,
          lat: response.position?.lat || response.items[0].position.lat,
          lng: response.position?.lng || response.items[0].position.lng,
          description: item.label,
        }

        track('escolherLocalizacao', { name: item.label, category: 'buscar' })

        onChangeLocation(location)
        setInputValue('')
      } catch (error) {
        console.error('cannot change location', error.message)
      }
    }
  }

  useEffect(() => {
    handleSearch(inputValue)
  }, [handleSearch, inputValue])

  useEffect(
    () => () => {
      handleSearch.cancel()
      trackSearch.cancel()
    },
    [handleSearch, trackSearch],
  )

  useEffect(() => {
    if (!inputValue) {
      setOptions([])
      onChangeValue?.(false)
    }

    if (inputValue) onChangeValue?.(true)
  }, [inputValue, onChangeValue])

  return (
    <Autocomplete
      open={inputValue.length >= MIN_SEARCH_LENGTH}
      autoComplete
      clearOnBlur
      className={classes.root}
      classes={{ paper: classes.paper, option: classes.option }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      filterOptions={(x) => x}
      options={options}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      renderInput={(params) => (
        <TextField
          {...params}
          className={classes.input}
          size={size ? size : 'medium'}
          label={label}
          placeholder={placeholder}
          color="primary"
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            style: { padding: 10 },
            startAdornment: <LocationOnIcon className={classes.inputIcon} />,
            endAdornment: (
              <div>
                {inputValue.length ? (
                  <IconButton size="small" onClick={handleReset}>
                    <Close fontSize="small" />
                  </IconButton>
                ) : null}
              </div>
            ),
          }}
        />
      )}
      ListboxProps={{ className: classes.paperContent }}
      renderOption={(option) => <AddressOption option={option} />}
      loading={loading}
      loadingText={`${getLiteral('1171')}...`}
      noOptionsText={
        isNoOptions && inputValue.length >= MIN_SEARCH_LENGTH ? getLiteral('2526') : `${getLiteral('1171')}...`
      }
    />
  )
}
